import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "../../components/Layout";
import { useInsuranceQuote, useUpdateInsuranceQuote } from "./insurance-quote";
import { Spinner } from "../../components/Spinner";
import classNames from "classnames";
import { Button } from "../../components/Button";
import { Address, User } from "../../types/User";
import { Link, useOutletContext } from "react-router";
import useLivlyUser from "../../context/UserProvider";
import { InsuranceQuoteContext } from "@/components/InsuranceQuoteLayout";
import {
  AssurantQuote,
  AssurantQuoteV2,
  SelectionMetaData
} from "@/types/Insurance";
import { useUpdateInsuranceV2Quote } from "./insurance-quote-v2";

function InsuranceAddressPage() {
  const { user } = useLivlyUser();
  const {
    data,
    isFetching,
    refetch,
    isAssurantV2,
    selectionMetaData,
    updateMetaData
  } = useOutletContext<InsuranceQuoteContext>();

  return (
    <Layout
      title="Select Address"
      back={{
        label: "Insurance",
        to: `../insurance-quote`
      }}
    >
      {isFetching ? (
        <div className="flex items-center justify-center flex-1 my-56">
          <Spinner color="livly" size="xl" />
        </div>
      ) : (
        <>
          {isAssurantV2 ? (
            <InsuranceAddressPageV2
              user={user}
              data={data as AssurantQuoteV2}
              refetch={refetch}
              selectionMetaData={selectionMetaData}
              updateMetaData={updateMetaData}
            />
          ) : (
            <InsuranceAddressPageV1
              user={user}
              data={data as AssurantQuote}
              refetch={refetch}
            />
          )}
        </>
      )}
    </Layout>
  );
}

const InsuranceAddressPageV1 = ({
  user,
  data,
  refetch
}: {
  user: User;
  data: AssurantQuote;
  refetch: () => void;
}) => {
  const updateQuoteMutation = useUpdateInsuranceQuote(user);

  const onSelectAddress = async (address: Address) => {
    await updateQuoteMutation.mutateAsync({ billingAddress: address });
    refetch();
  };
  return (
    <div className="max-w-4xl mx-auto">
      <p>
        You will receive information to this address within 10 business days.
        After you move in, we will automatically update this to your new
        address.
      </p>
      <div className="mt-4">
        <div className="space-y-4">
          {(user.addresses as Address[]).map((address: Address) => {
            const isSelected =
              data?.presentation.billingAddress?.id == address.id;
            const { address1, address2, city, state, zipCode } = address;

            return (
              <button
                disabled={updateQuoteMutation.isLoading}
                key={address.id}
                className={classNames(
                  { "border-red-400": isSelected },
                  "border rounded-lg p-4 w-full text-left hover:bg-slate-50 duration-300 transition"
                )}
                onClick={() => onSelectAddress(address)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-700">{address1}</p>
                    {address2 ? <p className="text-sm">{address2}</p> : null}
                    <p className="text-sm">
                      {city}, {state} {zipCode}
                    </p>
                  </div>
                  {isSelected ? (
                    <FontAwesomeIcon icon="check" className="text-red-400" />
                  ) : null}
                </div>
                {!address.isLeaseAddress ? (
                  <Link
                    to={`${address.id}`}
                    className="inline-flex mt-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button color="default">edit</Button>
                  </Link>
                ) : null}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end">
          <Link to="new">
            <button className="flex gap-2 items-center text-sm underline">
              <FontAwesomeIcon icon={["fal", "plus-circle"]} />
              <span>Add new address</span>
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const InsuranceAddressPageV2 = ({
  user,
  data,
  refetch,
  selectionMetaData,
  updateMetaData
}: {
  user: User;
  data: AssurantQuoteV2;
  refetch: () => void;
  selectionMetaData: SelectionMetaData | null;
  updateMetaData: (newMetaData: SelectionMetaData) => void;
}) => {
  const updateQuoteMutation = useUpdateInsuranceV2Quote(user);

  const onSelectAddress = async (address: Address) => {
    //@ts-ignore
    updateMetaData({
      ...selectionMetaData,
      billingAddress: address,
      isPlanAdded: false
    });
    await updateQuoteMutation.mutateAsync({
      newRequest: null,
      SelectionMetaData: {
        ...selectionMetaData,
        billingAddress: address,
        isPlanAdded: false
      }
    });
    refetch();
  };
  return (
    <div className="max-w-4xl mx-auto">
      <p>
        You will receive information to this address within 10 business days.
        After you move in, we will automatically update this to your new
        address.
      </p>
      <div className="mt-4">
        <div className="space-y-4">
          {(user.addresses as Address[]).map((address: Address) => {
            const isSelected =
              data?.selectionMetaData?.billingAddress?.id == address.id;
            const { address1, address2, city, state, zipCode } = address;

            return (
              <button
                disabled={updateQuoteMutation.isLoading}
                key={address.id}
                className={classNames(
                  { "border-red-400": isSelected },
                  "border rounded-lg p-4 w-full text-left hover:bg-slate-50 duration-300 transition"
                )}
                onClick={() => onSelectAddress(address)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-700">{address1}</p>
                    {address2 ? <p className="text-sm">{address2}</p> : null}
                    <p className="text-sm">
                      {city}, {state} {zipCode}
                    </p>
                  </div>
                  {isSelected ? (
                    <FontAwesomeIcon icon="check" className="text-red-400" />
                  ) : null}
                </div>
                {!address.isLeaseAddress ? (
                  <Link
                    to={`${address.id}`}
                    className="inline-flex mt-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button color="default">edit</Button>
                  </Link>
                ) : null}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end">
          <Link to="new">
            <button className="flex gap-2 items-center text-sm underline">
              <FontAwesomeIcon icon={["fal", "plus-circle"]} />
              <span>Add new address</span>
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export { InsuranceAddressPage };
